import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { lazy, Suspense } from "react";
import { AiLoader } from "@/components/AiLoader";
import { ScrollToTop } from "@/components/ScrollToTop";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Eager-load critical layout
const DashboardLayout = lazy(() => import("./components/DashboardLayout"));
const DashboardHomePage = lazy(() => import("./pages/DashboardHomePage"));

// Preload layout immediately for faster first navigation
import("./components/DashboardLayout");

// Preload common pages after idle
if (typeof window !== "undefined" && "requestIdleCallback" in window) {
  (window as any).requestIdleCallback(() => {
    import("./pages/DashboardHomePage");
    import("./pages/LeadsPage");
    import("./pages/CommerceOrdersPage");
  });
}
// Lazy-loaded pages
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const CreativeStudioPage = lazy(() => import("./pages/CreativeStudioPage"));
const FinancialPage = lazy(() => import("./pages/FinancialPage"));
const AdsPage = lazy(() => import("./pages/AdsPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
const ReorderPage = lazy(() => import("./pages/ReorderPage"));
const GlobalStatisticsPage = lazy(() => import("./pages/GlobalStatisticsPage"));
const AiToolsPage = lazy(() => import("./pages/AiToolsPage"));
const AiAssistantPage = lazy(() => import("./pages/AiAssistantPage"));
const StoreConnectionsPage = lazy(() => import("./pages/StoreConnectionsPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const CallCenterPage = lazy(() => import("./pages/CallCenterPage"));
const AgentSettingsPage = lazy(() => import("./pages/AgentSettingsPage"));
const UserSettingsPage = lazy(() => import("./pages/UserSettingsPage"));

const CommerceOrdersPage = lazy(() => import("./pages/CommerceOrdersPage"));
const CapitalDashboardPage = lazy(() => import("./pages/CapitalDashboardPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const Team = lazy(() => import("./pages/Team"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AdminCommandCenter = lazy(() => import("./pages/AdminCommandCenter"));
const Settings = lazy(() => import("./pages/Settings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <AiLoader text="Loading…" />
    </div>
  );
}

function ProtectedRoute({ children, allowExpired = false, ownerOnly = false, agentOnly = false, agentAllowed = false }: { children: React.ReactNode; allowExpired?: boolean; ownerOnly?: boolean; agentOnly?: boolean; agentAllowed?: boolean }) {
  const { user, loading, isTrialActive, subscriptionPlan, effectiveRole, accountStatus, impersonation } = useAuth();
  const role = effectiveRole;

  if (loading || !role) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;

  // Owner-only route guard
  if (ownerOnly && role !== "owner") return <Navigate to="/" replace />;

  // Agent-only route guard (owner can also access)
  if (agentOnly && role !== "agent" && role !== "owner") return <Navigate to="/dashboard" replace />;

  // Agents can now access most routes (blocked only from ownerOnly routes)

  // Block pending/rejected (owners bypass)
  if (role !== "owner" && accountStatus && accountStatus !== "approved") return <Navigate to="/" replace />;

  // Super Admin always bypasses trial/subscription
  if (role === "owner") return <>{children}</>;

  // During impersonation, bypass trial/subscription checks (owner is still behind the scenes)
  if (impersonation.active) return <>{children}</>;

  // Agent and User: must have active trial or subscription
  // But let pending/unapproved users through so DashboardLayout can show the pending screen
  if (!allowExpired && !isTrialActive && !subscriptionPlan && accountStatus === "approved") return <Navigate to="/pricing" replace />;

  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading, effectiveRole } = useAuth();
  const role = effectiveRole;
  if (loading) return <PageLoader />;
  if (!user) return <Auth />;
  if (role === "agent") return <Navigate to="/dashboard" replace />;
  return <Navigate to="/dashboard" replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Auth */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/auth" element={<HomeRoute />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Single shared DashboardLayout for all dashboard routes */}
              <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<DashboardHomePage />} />
                <Route path="/discovery" element={<DiscoveryPage />} />
                <Route path="/creative-studio" element={<CreativeStudioPage />} />
                <Route path="/financial" element={<FinancialPage />} />
                <Route path="/ads" element={<AdsPage />} />
                <Route path="/inventory" element={<InventoryPage />} />
                <Route path="/performance" element={<PerformancePage />} />
                <Route path="/reorder" element={<ReorderPage />} />
                <Route path="/global-statistics" element={<GlobalStatisticsPage />} />
                <Route path="/store-connections" element={<StoreConnectionsPage />} />
                <Route path="/leads" element={<LeadsPage />} />
                <Route path="/call-center" element={<CallCenterPage />} />
                <Route path="/commerce-orders" element={<CommerceOrdersPage />} />
                <Route path="/capital" element={<CapitalDashboardPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/ai-tools" element={<AiToolsPage />} />
                <Route path="/ai-assistant" element={<AiAssistantPage />} />
                <Route path="/team" element={<Team />} />
                <Route path="/command-center" element={<AdminCommandCenter />} />
                <Route path="/user-settings" element={<UserSettingsPage />} />
              </Route>

              {/* Owner-only routes */}
              <Route element={<ProtectedRoute ownerOnly><DashboardLayout /></ProtectedRoute>}>
                <Route path="/settings" element={<Settings />} />
              </Route>

              {/* Agent-only routes */}
              <Route element={<ProtectedRoute agentOnly><DashboardLayout /></ProtectedRoute>}>
                <Route path="/affiliate" element={<AffiliateDashboard />} />
              </Route>

              {/* Agent-allowed routes */}
              <Route element={<ProtectedRoute agentAllowed><DashboardLayout /></ProtectedRoute>}>
                <Route path="/agent-settings" element={<AgentSettingsPage />} />
              </Route>

              {/* Legacy redirects */}
              <Route path="/statistics" element={<Navigate to="/global-statistics" replace />} />
              <Route path="/analytics" element={<Navigate to="/global-statistics" replace />} />

              {/* Standalone */}
              <Route path="/pricing" element={<ProtectedRoute allowExpired><Pricing /></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ScrollToTop />
          <footer className="w-full py-3 text-center text-[11px] text-muted-foreground/80 border-t border-border/30">
            Powered by <span className="font-semibold text-primary">Vazoul</span> with <span className="text-destructive">♥</span>
          </footer>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
